import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaCoins, FaWallet, FaShoppingBag } from 'react-icons/fa';
import { API_BASE, capitalizeFirstLetter } from '../utils/helpers';

function payTone(status) {
  const s = status?.toLowerCase();
  if (s === 'paid') return 'bg-emerald-50 text-emerald-700';
  if (s === 'pending') return 'bg-amber-50 text-amber-700';
  if (s === 'refunded') return 'bg-rose-50 text-rose-700';
  return 'bg-slate-100 text-slate-600';
}

function StatTile({ icon: Icon, label, value, tone }) {
  return (
    <div className="ui-card flex items-center gap-3 p-4">
      <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${tone}`}>
        <Icon />
      </span>
      <div>
        <p className="text-xs font-medium text-slate-500">{label}</p>
        <p className="text-lg font-semibold text-slate-900">{value}</p>
      </div>
    </div>
  );
}

const UserDetails = () => {
  const { shopifyId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchUserDetails = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/user/${shopifyId}`);
      setUser(res.data.user);
      setOrders(res.data.orders || []);
      setError('');
    } catch (err) {
      console.error(err);
      setError('Failed to fetch user details');
    } finally {
      setLoading(false);
    }
  }, [shopifyId]);

  useEffect(() => { fetchUserDetails(); }, [fetchUserDetails]);

  const address = user
    ? [user.address1, user.address2, user.city, user.province, user.zip, user.country].filter(Boolean).join(', ')
    : '';
  const initial = (user?.name || user?.email || '?').trim().charAt(0).toUpperCase();

  return (
    <div>
      <button className="ui-btn-ghost mb-4 px-3 py-1.5 text-sm" onClick={() => navigate('/users')}>
        <FaArrowLeft className="text-xs" /> Back to Users
      </button>

      {loading ? (
        <div className="ui-card h-40 animate-pulse" />
      ) : error ? (
        <div className="ui-card p-6 text-sm text-rose-600">{error}</div>
      ) : !user ? (
        <div className="ui-card p-6 text-sm text-amber-600">User not found.</div>
      ) : (
        <div className="space-y-5">
          {/* Profile header */}
          <div className="ui-card p-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-indigo-600 text-xl font-semibold text-white">
                {initial}
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-slate-900">{user.name || 'Unnamed customer'}</h2>
                <p className="truncate text-sm text-slate-500">{user.email || '—'}</p>
              </div>
              <div className="ml-auto text-right text-sm text-slate-500">
                <div>Shopify ID <span className="font-mono text-slate-700">{shopifyId}</span></div>
                <div>Joined {user.shopify_created_at ? new Date(user.shopify_created_at).toLocaleDateString() : '—'}</div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatTile icon={FaCoins} label="Points" value={user.coins ?? 0} tone="bg-amber-50 text-amber-600" />
            <StatTile
              icon={FaWallet}
              label="Total Spent"
              value={`QAR ${parseFloat(user.shopify_total_spent || 0).toFixed(2)}`}
              tone="bg-emerald-50 text-emerald-600"
            />
            <StatTile icon={FaShoppingBag} label="Orders" value={user.shopify_orders_count ?? 0} tone="bg-indigo-50 text-indigo-600" />
          </div>

          {/* Contact + address */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="ui-card p-5">
              <h3 className="mb-3 text-sm font-semibold text-slate-900">Contact</h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-4"><dt className="text-slate-500">Phone</dt><dd className="text-slate-800">{user.mobile || '—'}</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-slate-500">Email</dt><dd className="truncate text-slate-800">{user.email || '—'}</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-slate-500">Device ID</dt><dd className="truncate font-mono text-xs text-slate-600">{user.device_id || '—'}</dd></div>
              </dl>
            </div>
            <div className="ui-card p-5">
              <h3 className="mb-3 text-sm font-semibold text-slate-900">Address</h3>
              <p className="text-sm text-slate-700">{address || '—'}</p>
            </div>
          </div>

          {/* Orders */}
          <div className="ui-card overflow-hidden">
            <div className="border-b border-slate-200 px-5 py-3">
              <h3 className="text-sm font-semibold text-slate-900">Orders</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="ui-table">
                <thead>
                  <tr>
                    <th className="w-12">#</th>
                    <th>Order ID</th>
                    <th>Total</th>
                    <th>Payment</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0 ? (
                    <tr><td colSpan={5} className="py-10 text-center text-slate-400">No orders found.</td></tr>
                  ) : (
                    orders.map((order, idx) => (
                      <tr key={order.id}>
                        <td className="text-slate-400">{idx + 1}</td>
                        <td className="font-medium text-slate-900">{order.name || order.id}</td>
                        <td className="whitespace-nowrap font-medium text-slate-900">QAR {parseFloat(order.total_price || 0).toFixed(2)}</td>
                        <td><span className={`ui-badge ${payTone(order.financial_status)}`}>{capitalizeFirstLetter(order.financial_status || 'unknown')}</span></td>
                        <td className="whitespace-nowrap text-slate-500">{order.created_at ? new Date(order.created_at).toLocaleString() : '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetails;
